import { useFirmVisibilityStore } from '@js/stores/firmVisibilityStore.js';
import { useProjectCandidatesStore } from '@js/stores/projectCandidatesStore.js';
import billingPermissions from '@js/nal/vue-mixins/billingPermissions.js';

import { hideablesDetailsListNested } from '@js/client-portal/constants/overrides.js';
import {
    dashboardChartKey,
    dashboardContentKeys,
    sharedSettings,
    cpKeyMappings,
    hideableLinkedTabs,
    projectTypeButtonConfigs,
    customFieldCandidatesMappings,
    reportKeyMappings,
    mandatoryFieldsKeyMappings,
} from '@js/nal/vue-mixins/visibilitySettingsKeyMappings.js';

export default {
    mixins: [
        billingPermissions,
    ],
    data() {
        return {
            processing: false,
            // This will later come from projectTypes store
            projectTypes: ['lists', 'opportunities', 'assignments'],
            potentialMandatoryFields: ['compensation', 'email', 'addresses', 'billing'],
            projectTypeButtonConfigs: projectTypeButtonConfigs,
            cpKeyMappings: cpKeyMappings,
            customFieldCandidatesMappings: customFieldCandidatesMappings,
            reportKeyMappings,
            mandatoryFieldsKeyMappings,
            modelTypeMappings: {},
        };
    },
    computed: {
        candidateStore() {
            return useProjectCandidatesStore();
        },
        firmVisibilityStore() {
            return useFirmVisibilityStore();
        },
        peopleProjectTypes() {
            return this.projectTypes.map(projectType => projectType.singular().concat('People'));
        },
        activeChartKey() {
            return dashboardChartKey[this.dataType];
        },
        hideableSettingsTab() {
            return {
                ...Object.fromEntries(
                    this.projectTypes.map(type => [type, { ...sharedSettings }])
                ),
                people: { tasks: 'tasks' },
                // Add companies later if tasks added in companies settings modal
            };
        },
        hiddenFields() {
            const infoPanelFields = this.featuresConfig?.infoPanelSettings?.overview || {};
            const allTabs = this.featuresConfig?.tabs || {};
            return [...this.getHiddenFields(infoPanelFields), ...this.getHiddenFields(allTabs, true)];
        },
        hiddenPeopleCpFields() {
            const config = this.modelTypeSettings.people;
            const infoPanelFields = config?.infoPanelSettings?.overview || {};
            const allTabs = config?.tabs || {};
            return [...this.getHiddenFields(infoPanelFields), ...this.getHiddenFields(allTabs, true)];
        },
        // Separate hiddenFields created for CP since mappings is different
        hiddenCpFields() {
            return this.hiddenFields.map(field => cpKeyMappings[field])
                .filter(field => field !== undefined);
        },
        hiddenDashboardContents() {
            const dashboardContentKeyMappings = {
                'chart': this.activeChartKey,
                ...dashboardContentKeys,
            };
            return Object.keys(dashboardContentKeyMappings)?.filter(content => {
                // If candidates dashboard inside each project types and in CP
                if (this.peopleProjectTypes.includes(this.dataType) || this.dataType === 'cp-candidates') {
                    return this.isFieldHiddenByType(dashboardContentKeyMappings[content], 'people');
                }
                return this.isFieldHidden(dashboardContentKeyMappings[content]);
            });
        },
        hiddenCurrentSettingsTabs() {
            const currentSettingsTab = this.hideableSettingsTab[`${this.dataType}`];
            const filteredTabs = _.keys(currentSettingsTab)
                .filter(key => _.includes(this.hiddenFields, currentSettingsTab[key]));
            // In case of 'candidates' (Pipeline Tags), only hide the whole tab if hidden on all project types
            if (this.projectTypes.every(type =>
                this.isFieldHiddenByType(this.getCandidateKeyByType(type), type))) {
                return filteredTabs.concat('candidates');
            }
            return filteredTabs;
        },
        projectTypesByVisibleCandidate() {
            return this.projectTypes.filter(type =>
                !this.isFieldHiddenByType('candidates', type))
                .map(type => type.singular());
        },
        modelTypeSettings() {
            return this.firmVisibilityStore.modelTypeSettings;
        },
        featuresConfig() {
            return this.getConfigByDataType(this.dataType);
        },
        overviewSettings() {
            return this.featuresConfig.infoPanelSettings.overview;
        },
        fullViewTabs() {
            return Object.keys(this.featuresConfig.tabs).filter((tab) => {
                // Exclude the 'clientPortal' since it's only for settings

                if (tab === 'clientPortal') {
                    return false;
                }
                if (tab === 'unconnected') {
                    return this.featuresConfig.tabs[tab] && this.hasUnconnectedCandidates;
                }
                if (!this.tabPermissionCheck(tab)) {
                    return false;
                }
                if (this.dataType === 'people') {
                    if (tab === 'companies') {
                        return !this.isFieldHiddenByType('positions', 'people');
                    }
                    if (this.projectTypes.includes(tab)) {
                        return ['candidates', 'contacts'].some(innerTab => {
                            const fieldKey = innerTab === 'candidates'
                                ? this.getCandidateKeyByType(tab)
                                : innerTab;
                            return !this.isFieldHiddenByType(fieldKey, tab);
                        });
                    }
                }
                return this.featuresConfig.tabs[tab] && this.tabPermissionCheck(tab);
            });
        },
        existingMandatoryFields() {
            // TODO: Expand to other types by removing filter project types if required
            return this.nal.firm.mandatory_fields
                .filter(item => [...this.projectTypes, 'clients', 'people'].includes(item.type))
                .reduce((result, fieldObj) => {
                    const modelType = fieldObj.type === 'clients'
                        ? 'companies'
                        : fieldObj.type;
                    result[modelType] = Object.keys(fieldObj.fields).filter(fieldKey => {
                        if (!this.potentialMandatoryFields.includes(fieldKey)) {
                            return false;
                        }
                        const fieldValue = fieldObj.fields[fieldKey];
                        if (
                            fieldValue
                            && typeof fieldValue === 'object'
                            && 'required' in fieldValue
                        ) {
                            return fieldValue.required === true;
                        }
                        return typeof fieldValue === 'object';
                    });
                    return result;
                }, {});
        },
        // linkedin extension
        hiddenLinkedinTabs() {
            const result = {};
            for (const type in hideableLinkedTabs) {
                result[type] = Object.keys(hideableLinkedTabs[type]).filter(key =>
                    this.isFieldHiddenByType(hideableLinkedTabs[type][key], type)
                );
            }
            return result;
        },
        hasCustomFieldsType() {
            switch (this.dataType) {
                case 'companies':
                    return (this.nal.customFields?.clients || []).length > 0;
                case 'people':
                    return (this.nal.customFields?.people || []).length > 0;
                default:
                    return (this.nal.customFields?.projects || []).length > 0;
            }
        },
        hasUnconnectedCandidates() {
            return (this.candidateStore?.candidates?.meta?.unconnectedCandidates || []).length > 0;
        },
    },
    methods: {
        async getVisibilitySettings() {
            try {
                this.processing = true;
                const response = await this.get('api/settings/visibility-settings');
                this.firmVisibilityStore.setVisibilitySettings(response);
            } finally {
                this.processing = false;
            }
        },
        getCandidateKeyByType(datatype) {
            return ['list', 'lists'].includes(datatype)
                ? 'people'
                : 'candidates';
        },
        getModelType(dataType) {
            if (this.projectTypes.includes(dataType)) {
                return 'projects';
            } else if (dataType === 'clients') {
                return 'companies';
            }
            return dataType;
        },
        getConfigByDataType(dataType) {
            return this.getModelType(dataType) === 'projects'
                ? this.modelTypeSettings?.projects?.[dataType]
                : this.modelTypeSettings?.[dataType];
        },
        getHiddenFields(allFields, isTabConfig = false) {
            return Object.keys(allFields || {}).filter(field =>
                isTabConfig ? allFields[field] !== true : allFields[field]?.visible !== true
            );
        },
        isFieldHidden(field) {
            return this.hiddenFields?.includes(field);
        },
        isFieldHiddenByType(field, dataType) {
            const config = this.getConfigByDataType(dataType);
            const infoPanelFields = config?.infoPanelSettings?.overview || {};
            const allTabs = config?.tabs || {};
            const allHiddenFields = [...this.getHiddenFields(infoPanelFields), ...this.getHiddenFields(allTabs, true)];

            //  Special case for People field on lists
            // :TODO (Remove this in future)
            if (field === 'candidates' && dataType === 'lists') {
                field = 'people';
            }
            return allHiddenFields.includes(field);
        },
        isSectionVisible(section, dataType = null) {
            return dataType
                ? this.modelTypeSettings[dataType]?.infoPanelSettings.overview[section].visible
                : this.overviewSettings[section].visible;
        },
        getSectionLabel(section) {
            const label = this.overviewSettings[section].label;
            const modelType = this.getModelType(this.dataType);
            if (!label) {
                return trans(`visibility.${modelType}.info-panel.section.${section}`);
            } else if (typeof label === 'string') {
                return label;
            }
            return label[this.nal.locale];
        },
        getHideableCpKey(field) {
            const match = _.find(hideablesDetailsListNested, item => {
                if (_.isObject(item)) {
                    // Direct match with value
                    if (item.value === field) {
                        return true;
                    }
                    // Match inside subValues
                    return _.includes(item.subValues, field);
                }
                // Direct string match which doesnt have any object
                return item === field;
            });

            if (_.isObject(match)) {
                return match.value;
            }
            if (_.isString(match)) {
                return match;
            }
            return null;
        },
        // Part of filterReportKeys
        shouldSkipKey(key, currentPath, conditions) {
            const condition = conditions[currentPath] ?? conditions[key];
            return typeof condition === 'boolean' && condition;
        },
        // For Word and Excel Reports Keys filtering
        filterReportKeys(executiveKeys, conditions, path = '') {
            const result = {};
            for (const key in executiveKeys) {
                const value = executiveKeys[key];
                const currentPath = path ? `${path}.${key}` : key;

                if (this.shouldSkipKey(key, currentPath, conditions)) {
                    continue;
                }

                if (value && typeof value === 'object' && !Array.isArray(value)) {
                    const filtered = this.filterReportKeys(value, conditions, currentPath);
                    if (Object.keys(filtered).length > 0) {
                        result[key] = filtered;
                    }
                } else {
                    result[key] = value;
                }
            }
            return result;
        },
        setupModelMappings() {
            this.modelTypeMappings = {
                projects: this.projectTypes,
                all: [...this.projectTypes, 'people', 'companies'],
                lists: ['lists'],
                opportunities: ['opportunities'],
                assignments: ['assignments'],
                companies: ['companies'],
                people: ['people'],
            };
        },
        getFilteredReportKeys(keys, hideableKeys, modelType = null) {
            let hiddenKeys = {};
            if (modelType) {
                for (const [key, value] of Object.entries(hideableKeys)) {
                    if (this.isFieldHiddenByType(value, modelType)) {
                        hiddenKeys[key] = true;
                    }
                }
            } else {
                for (const [groupName, groupKeys] of Object.entries(hideableKeys)) {
                    const modelTypes = this.modelTypeMappings[groupName] || [];

                    for (const [key, value] of Object.entries(groupKeys)) {
                        const isHiddenForAll = modelTypes.every(type =>
                            this.isFieldHiddenByType(value, type)
                        );

                        if (isHiddenForAll) {
                            hiddenKeys[key] = true;
                        }
                    }
                }
            }
            return this.filterReportKeys(keys, hiddenKeys);
        },
        getProjectFilterParamsByContext(context) {
            if (!context) {
                return '';
            }

            const filterParams = new URLSearchParams();
            const availableProjectTypes = this.projectTypes.filter(type => {
                if (context === 'companies') {
                    return !this.isFieldHiddenByType(type, context);
                }
                return !this.isFieldHiddenByType(context, type);
            }).map(type => type.singular());

            //   if candidates/people or projects visible on all projectTypes or companies, doesn't need filtering
            if (availableProjectTypes.length === this.projectTypes.length) {
                return '';
            }

            availableProjectTypes.forEach(type => {
                const key = 'is' + type.charAt(0).toUpperCase() + type.slice(1);
                filterParams.set(key, true);
            });

            return '&' + filterParams.toString();
        },
        tabPermissionCheck(tab) {
            const permissions = {
                custom: () => this.hasCustomFieldsType,
                assignments: () => auth('see', 'assignments'),
                opportunities: () => auth('see', 'opportunities'),
                lists: () => auth('see', 'lists'),
                boardex: () => this.nal.firm.boardex,
                altrata: () => this.nal.firm.altrata,
                companies: () => auth('see', 'companies'),
                similar: () => auth('see', 'search'),
                billing: () => this.billingPermission('see'),
                search: () => auth('see', 'search'),
                target: () => auth('see', 'companies'),
            };
            return permissions[tab]?.() ?? true;
        },
        syncCardSlotsWithVisibility(customizations) {
            if (!customizations) {
                return;
            }

            for (const [viewKey, card] of Object.entries(customizations)) {
                if (viewKey.includes('grid')) {
                    continue;
                }

                for (const [slotKey, slotContent] of Object.entries(card.cardData)) {
                    const slotNotVisible = !slotContent?.slot || slotContent.slot === 'hide'
                        || !this.hiddenDashboardContents.includes(slotKey);

                    const slotTaken = Object.entries(card.cardData).some(
                        ([existingKey, existingContent]) =>
                            existingKey !== slotKey
                            && existingContent?.slot === slotContent.slot
                            && !this.hiddenDashboardContents.includes(existingKey)
                    );

                    if (!slotTaken || slotNotVisible) {
                        continue;
                    }

                    window.deleteKeyFromVueObject(slotContent, 'slot');
                    window.setVueObject(slotContent, 'visible', false);
                }
            }
        },
    },
    created() {
        this.setupModelMappings();
    },
};